<template>
  <div class="field-details">
    <div class="back-header">
      <button @click="$emit('close')" class="back-button">
        <i class="fa fa-arrow-left"></i>
        Back to Farm Fields
      </button>
    </div>

    <div v-if="loading" class="loading-container">
      <i class="fa fa-circle-o-notch fa-spin fa-3x fa-fw"></i>
      <p>Loading field details...</p>
    </div>

    <div v-else-if="error" class="error-container">
      <i class="fa fa-exclamation-triangle fa-3x"></i>
      <p>{{ error }}</p>
      <button @click="retry" class="retry-button">Retry</button>
    </div>

    <template v-else-if="fieldData">
      <div class="field-header">
        <div class="field-title">
          <h2>{{ fieldData.field.name || 'Unnamed Field' }}</h2>
          <span class="field-id">ID: {{ fieldData.field.id }}</span>
        </div>
        <div class="field-meta">
          <span class="meta-badge" v-if="fieldData.field.state">
            <i class="fa fa-map-marker"></i>
            {{ fieldData.field.state }}
            <span v-if="fieldData.field.county">, {{ fieldData.field.county }}</span>
          </span>
        </div>
      </div>

      <div class="stats-grid">
        <div class="stat-card">
          <div class="stat-icon"><i class="fa fa-expand"></i></div>
          <div class="stat-content">
            <div class="stat-label">Total Acres</div>
            <div class="stat-value">{{ activeTotalAcres }}</div>
          </div>
        </div>
      </div>

      <div class="details-section">
        <h3>Field Active Layout</h3>
        <div class="properties-grid">
          <div class="property-item" v-if="fieldData.activeLayout.activeLayoutId">
            <span class="property-label">Active Layout:</span>
            <span class="property-value">{{
              fieldData.activeLayout?.name || 'Unnamed Layout'
            }}</span>
          </div>
          <div class="property-item" v-if="fieldData.activeLayout.cropType">
            <span class="property-label">Crop Type:</span>
            <span class="property-value">{{ fieldData.activeLayout.cropType || 'Unknown' }}</span>
          </div>
          <div class="property-item" v-if="fieldData.activeLayout.grossAppliedInches">
            <span class="property-label">Field Mode:</span>
            <span class="property-value">{{ activeFieldMode }}</span>
          </div>
          <div class="property-item" v-if="fieldData.activeLayout.furrowSpacingInInches">
            <span class="property-label">Furrow Spacing:</span>
            <span class="property-value">{{ fieldData.activeLayout.furrowSpacingInInches }}"</span>
          </div>
          <div class="property-item" v-if="fieldData.activeLayout.fallPerLeveeInInches">
            <span class="property-label">Fall Per Levee:</span>
            <span class="property-value">{{ fieldData.activeLayout.fallPerLeveeInInches }}"</span>
          </div>
          <div class="property-item" v-if="fieldData.activeLayout.leveeHeightInInches">
            <span class="property-label">Levee Height:</span>
            <span class="property-value">{{ fieldData.activeLayout.leveeHeightInInches }}"</span>
          </div>
          <div class="property-item" v-if="activeTotalSets">
            <span class="property-label">Number of Sets:</span>
            <span class="property-value">{{ activeTotalSets }}</span>
          </div>
        </div>
      </div>

      <!-- Irrigation Systems -->
      <div class="details-section" v-if="fieldData.irrigationSystems.length > 0">
        <h3>Irrigation Systems</h3>
        <div class="irrigation-list">
          <div
            v-for="system in fieldData.irrigationSystems"
            :key="system.id"
            class="irrigation-card"
          >
            <div class="irrigation-header">
              <div class="irrigation-title">
                <h4>{{ system.name || 'Unnamed System' }}</h4>
                <span class="irrigation-id">ID: {{ system.id }}</span>
              </div>
            </div>
            <div class="irrigation-body">
              <div
                class="flow-rates-section"
                v-if="system.flowRates && system.flowRates.length > 0"
              >
                <div class="section-label">
                  <i class="fa fa-tint"></i>
                  Flow Rates
                </div>
                <div class="flow-rates-grid">
                  <div
                    v-for="flowRate in system.flowRates"
                    :key="flowRate.id"
                    class="flow-rate-badge"
                  >
                    <div class="flow-rate-name">{{ flowRate.name || 'Unnamed' }}</div>
                    <div class="flow-rate-value">
                      <span class="value">{{ flowRate.valueInGpm }}</span>
                      <span class="unit">GPM</span>
                    </div>
                    <div class="flow-rate-watering-time">
                      <i class="fa fa-clock-o"></i>
                      {{ calculateWateringTime(flowRate) }}
                    </div>
                  </div>
                </div>
              </div>

              <div
                class="pipe-types-section"
                v-if="system.pipeTypeDetails && system.pipeTypeDetails.length > 0"
              >
                <div class="section-label">
                  <i class="fa fa-exchange"></i>
                  Pipe Types
                </div>
                <div class="pipe-types-grid">
                  <div
                    v-for="(detail, index) in system.pipeTypeDetails"
                    :key="`${detail.pipeTypeName}-${detail.flowRateId}-${index}`"
                    class="pipe-type-badge"
                  >
                    <div class="pipe-type-name">{{ detail.pipeTypeName }}</div>
                    <div class="pipe-type-flow-rate">
                      {{ detail.flowRateName || 'Unnamed' }} ({{ detail.flowRateValue }} GPM)
                    </div>
                    <div class="pipe-type-value">
                      <span class="value">{{ Math.round(detail.length) }}</span>
                      <span class="unit">ft</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script setup>
import { computed, inject } from 'vue'
import useSWRV from 'swrv'
import * as WateringTimeAlg from '../util/WateringTimeAlg.js'

const $api = inject('$api')

const props = defineProps({
  fieldId: {
    type: String,
    required: true
  }
})

const emit = defineEmits(['close'])

const fetcher = async (url) => {
  const response = await $api.fetch(url)
  if (!response.ok) {
    throw new Error('Failed to fetch field details')
  }
  return response.json()
}

const apiEndpoint = computed(() => {
  return `/api/reporting/user_acres/summary/field/${props.fieldId}`
})

const {
  data: fieldData,
  error: fetchError,
  isLoading,
  isValidating,
  mutate
} = useSWRV(apiEndpoint, fetcher, {
  revalidateOnFocus: false
})

const loading = computed(() => isLoading.value || isValidating.value)
const error = computed(() => {
  if (fetchError.value) return 'Failed to load field details'
  return null
})

const activeFieldMode = computed(() => {
  if (!fieldData.value?.field) return null
  const mode = fieldData.value.activeLayout?.mode
  if (mode === 1) return 'Furrow'
  if (mode === 2) return 'Levee'
  return null
})

const activeTotalAcres = computed(() => {
  if (!fieldData.value?.activeHoleDesign?.acres) return '0.00'
  const total = fieldData.value.activeHoleDesign.acres
  return total.toFixed(2)
})

const activeTotalSets = computed(() => {
  const numberOfSets = fieldData.value?.activeLayout?.furrowSetDetails?.resultingFurrowSets?.length;
  if (!numberOfSets) return '0'
  return numberOfSets
})

const calculateWateringTime = computed(() => {
  return (flowRate) => {
    if (!fieldData.value?.activeHoleDesign?.acres || !fieldData.value?.activeLayout) {
      return 'N/A'
    }
    //watered area is iffy need to confirm
    const wateredAreaInAcres = fieldData.value.activeHoleDesign.acres
    const fieldLayout = fieldData.value.activeLayout

    const hours = WateringTimeAlg.wateringTimeInHours(wateredAreaInAcres, flowRate, fieldLayout)

    if (hours == null) {
      return 'N/A'
    }

    return hours.toFixed(2) + ' hrs'
  }
})

function retry() {
  mutate()
}

function formatDate(dateString) {
  if (!dateString) return '-'
  const date = new Date(dateString)
  return date.toLocaleDateString()
}
</script>

<style scoped>
.field-details {
  padding: var(--general-padding);
}

/* Back Button */
.back-header {
  margin-bottom: 24px;
}

.back-button {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  background: var(--menu-background-color);
  border: 1px solid var(--accent0);
  border-radius: var(--general-border-radius);
  color: var(--accent0);
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s;
}

.back-button:hover {
  background: var(--accent0);
  color: white;
}

/* Loading and Error States */
.loading-container,
.error-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  color: var(--accent0);
}

.loading-container p,
.error-container p {
  margin-top: 16px;
  font-size: 16px;
}

.error-container {
  color: #ff6b6b;
}

.retry-button {
  margin-top: 16px;
  padding: 10px 20px;
  background: var(--accent0);
  border: none;
  border-radius: var(--general-border-radius);
  color: white;
  cursor: pointer;
}

.retry-button:hover {
  opacity: 0.8;
}

/* Field Header */
.field-header {
  margin-bottom: 32px;
}

.field-title {
  display: flex;
  align-items: baseline;
  gap: 16px;
  margin-bottom: 12px;
}

.field-title h2 {
  margin: 0;
  color: var(--accent0);
  font-size: 28px;
}

.field-id {
  font-size: 14px;
  color: #999;
  font-family: monospace;
}

.field-meta {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
}

.meta-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  background: var(--dark0);
  border-radius: var(--general-border-radius);
  font-size: 13px;
  color: var(--text1);
}

/* Stats Grid */
.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
  margin-bottom: 32px;
}

.stat-card {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 20px;
  background: var(--dark0);
  border-radius: var(--general-border-radius);
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.stat-icon {
  font-size: 32px;
  color: var(--accent0);
  opacity: 0.8;
}

.stat-content {
  flex: 1;
}

.stat-label {
  font-size: 12px;
  color: #999;
  text-transform: uppercase;
  margin-bottom: 4px;
}

.stat-value {
  font-size: 24px;
  font-weight: bold;
  color: var(--accent1);
}

/* Details Sections */
.details-section {
  margin-bottom: 32px;
}

.details-section h3 {
  margin: 0 0 16px 0;
  color: var(--accent0);
  font-size: 18px;
  padding-bottom: 8px;
  border-bottom: 2px solid var(--accent0);
}

/* Properties Grid */
.properties-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 8px;
  padding: 16px;
  background: var(--dark0);
  border-radius: var(--general-border-radius);
}

.property-item {
  display: flex;
  align-items: center;
  gap: 0 4px;
  padding: 8px;
}

.property-label {
  font-weight: 500;
  color: #999;
}

.property-value {
  color: var(--text1);
  font-family: monospace;
}

/* Tables */
.table-container {
  overflow-x: auto;
  background: var(--dark0);
  border-radius: var(--general-border-radius);
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.details-table {
  width: 100%;
  border-collapse: collapse;
}

.details-table thead {
  background: rgba(0, 0, 0, 0.3);
  border-bottom: 2px solid var(--accent0);
}

.details-table th {
  padding: 12px 16px;
  text-align: left;
  font-weight: bold;
  text-transform: uppercase;
  font-size: 12px;
  color: #999;
}

.details-table tbody tr {
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
}

.details-table tbody tr:last-child {
  border-bottom: none;
}

.details-table tbody tr:hover {
  background-color: rgba(29, 154, 214, 0.05);
}

.details-table td {
  padding: 12px 16px;
  color: var(--text1);
}

.status-badge {
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: bold;
  text-transform: uppercase;
}

.status-badge.success {
  background: rgba(46, 204, 113, 0.2);
  color: #2ecc71;
}

.status-badge.error {
  background: rgba(255, 107, 107, 0.2);
  color: #ff6b6b;
}

/* Irrigation Systems */
.irrigation-list {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 20px;
}

.irrigation-card {
  background: var(--dark0);
  border-radius: var(--general-border-radius);
  border: 1px solid rgba(255, 255, 255, 0.1);
  overflow: hidden;
  transition:
    border-color 0.2s,
    box-shadow 0.2s;
}

.irrigation-card:hover {
  border-color: var(--accent0);
  box-shadow: 0 4px 12px rgba(29, 154, 214, 0.15);
}

.irrigation-header {
  padding: 16px;
  background: rgba(0, 0, 0, 0.3);
  border-bottom: 2px solid var(--accent0);
}

.irrigation-title {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.irrigation-title h4 {
  margin: 0;
  color: var(--accent0);
  font-size: 18px;
  font-weight: 600;
}

.irrigation-id {
  font-size: 11px;
  color: #999;
  font-family: monospace;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.irrigation-body {
  padding: 16px;
}

.flow-rates-section {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.section-label {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  font-weight: 600;
  color: #999;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 4px;
}

.section-label i {
  color: var(--accent0);
  font-size: 14px;
}

.flow-rates-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
  gap: 12px;
}

.flow-rate-badge {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 12px;
  background: rgba(29, 154, 214, 0.1);
  border: 1px solid rgba(29, 154, 214, 0.3);
  border-radius: var(--general-border-radius);
  transition: all 0.2s;
}

.flow-rate-badge:hover {
  background: rgba(29, 154, 214, 0.15);
  border-color: var(--accent0);
  transform: translateY(-2px);
}

.flow-rate-name {
  font-size: 12px;
  color: #999;
  font-weight: 500;
  text-transform: capitalize;
}

.flow-rate-value {
  display: flex;
  align-items: baseline;
  gap: 4px;
}

.flow-rate-value .value {
  font-size: 20px;
  font-weight: bold;
  color: var(--accent1);
}

.flow-rate-value .unit {
  font-size: 11px;
  color: #999;
  font-weight: 600;
  text-transform: uppercase;
}

.flow-rate-watering-time {
  font-size: 11px;
  color: #4db8ff;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 4px;
  margin-top: 4px;
  opacity: 0.9;
}

.flow-rate-watering-time i {
  font-size: 10px;
}

/* Pipe Types Section */
.pipe-types-section {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 20px;
  padding-top: 20px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
}

.pipe-types-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
  gap: 12px;
}

.pipe-type-badge {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 12px;
  background: rgba(156, 39, 176, 0.1);
  border: 1px solid rgba(156, 39, 176, 0.3);
  border-radius: var(--general-border-radius);
  transition: all 0.2s;
}

.pipe-type-badge:hover {
  background: rgba(156, 39, 176, 0.15);
  border-color: #9c27b0;
  transform: translateY(-2px);
}

.pipe-type-name {
  font-size: 12px;
  color: #999;
  font-weight: 500;
  text-transform: capitalize;
}

.pipe-type-flow-rate {
  font-size: 10px;
  color: #ba68c8;
  font-weight: 400;
  margin-top: 2px;
  opacity: 0.8;
}

.pipe-type-value {
  display: flex;
  align-items: baseline;
  gap: 4px;
}

.pipe-type-value .value {
  font-size: 20px;
  font-weight: bold;
  color: #ba68c8;
}

.pipe-type-value .unit {
  font-size: 11px;
  color: #999;
  font-weight: 600;
  text-transform: uppercase;
}
</style>
